import { forwardRef, type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes } from 'react'

/**
 * Primitivas de interfaz. Fondos siempre claros; el azul marino es el único
 * "oscuro" permitido, y solo en elementos contenidos (nunca de página
 * completa) — regla de marca. Objetivos táctiles ≥44px (WCAG 2.5.5).
 */

type VarianteBoton = 'primario' | 'secundario' | 'peligro' | 'fantasma'

export const Boton = forwardRef<HTMLButtonElement, ButtonHTMLAttributes<HTMLButtonElement> & { variante?: VarianteBoton; cargando?: boolean }>(
  ({ variante = 'primario', cargando, className = '', children, disabled, ...props }, ref) => {
    const base =
      'objetivo-tactil foco-visible inline-flex items-center justify-center gap-2 rounded-[var(--radius-rumbo)] px-5 py-2.5 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50'
    const variantes: Record<VarianteBoton, string> = {
      primario: 'bg-oro text-azul hover:brightness-95 active:brightness-90 font-semibold',
      secundario: 'bg-azul text-white hover:brightness-110 active:brightness-95',
      peligro: 'bg-critico text-white hover:brightness-110',
      fantasma: 'bg-transparent text-azul border border-borde-fuerte hover:bg-superficie-alt',
    }
    return (
      <button ref={ref} className={`${base} ${variantes[variante]} ${className}`} disabled={disabled || cargando} {...props}>
        {cargando && <span className="size-4 animate-spin rounded-full border-2 border-current border-t-transparent" aria-hidden />}
        {children}
      </button>
    )
  },
)
Boton.displayName = 'Boton'

export function Tarjeta({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <div className={`rounded-[var(--radius-rumbo-lg)] bg-superficie shadow-[var(--mppt-sombra)] ${className}`}>{children}</div>
}

export function Etiqueta({ children, htmlFor, obligatorio, ayuda }: { children: ReactNode; htmlFor?: string; obligatorio?: boolean; ayuda?: string }) {
  return (
    <label htmlFor={htmlFor} className="mb-1.5 block text-[15px] font-medium text-texto">
      {children}
      {obligatorio && (
        <span className="ml-1 text-rojo" aria-hidden>
          *
        </span>
      )}
      {ayuda && <span className="mt-0.5 block text-sm font-normal text-texto-suave">{ayuda}</span>}
    </label>
  )
}

// Sin `w-full` acá: Tailwind no tiene conflict resolution — si el consumidor
// pasa className="w-28" o "flex-1", el `w-full` del CSS igual gana (último en
// el stylesheet) y el override queda sin efecto. Aplicamos w-full solo cuando
// el consumidor no definió ningún ancho. Sin tailwind-merge para no sumar dep.
const claseCampoBase =
  'objetivo-tactil foco-visible rounded-[var(--radius-rumbo)] border border-borde-fuerte bg-white px-3.5 py-2.5 text-base text-texto placeholder:text-texto-tenue disabled:bg-superficie-alt'

const tieneAnchoOverride = (c: string) => /(^|\s)(w-|flex-\d|flex-1(\s|$)|min-w-|max-w-)/.test(c)
const anchoPorDefecto = (c: string) => (tieneAnchoOverride(c) ? '' : 'w-full')

export const Entrada = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement> & { error?: string }>(({ className = '', error, ...props }, ref) => (
  <input ref={ref} className={`${claseCampoBase} ${anchoPorDefecto(className)} ${error ? 'border-critico' : ''} ${className}`} aria-invalid={Boolean(error)} {...props} />
))
Entrada.displayName = 'Entrada'

export const Seleccion = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement> & { error?: string }>(({ className = '', error, children, ...props }, ref) => (
  <select ref={ref} className={`${claseCampoBase} ${anchoPorDefecto(className)} ${error ? 'border-critico' : ''} ${className}`} aria-invalid={Boolean(error)} {...props}>
    {children}
  </select>
))
Seleccion.displayName = 'Seleccion'

export function MensajeError({ children }: { children?: ReactNode }) {
  if (!children) return null
  return (
    <p className="mt-1.5 flex items-start gap-1.5 text-sm text-critico" role="alert">
      <svg viewBox="0 0 20 20" fill="currentColor" className="mt-0.5 size-4 shrink-0" aria-hidden>
        <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.169 2.63-1.515 2.63H3.72c-1.346 0-2.188-1.463-1.515-2.63L8.485 2.495ZM10 6a.75.75 0 0 1 .75.75v3.5a.75.75 0 0 1-1.5 0v-3.5A.75.75 0 0 1 10 6Zm0 8a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z" clipRule="evenodd" />
      </svg>
      {children}
    </p>
  )
}

export function MensajeAyuda({ children }: { children?: ReactNode }) {
  if (!children) return null
  return <p className="mt-1.5 text-sm text-texto-suave">{children}</p>
}

export function Insignia({ tono, children }: { tono: 'verde' | 'amarillo' | 'rojo' | 'azul'; children: ReactNode }) {
  const tonos: Record<string, string> = {
    verde: 'bg-exito-fondo text-exito',
    amarillo: 'bg-advertencia-fondo text-advertencia',
    rojo: 'bg-critico-fondo text-critico',
    azul: 'bg-info-fondo text-info',
  }
  return <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium ${tonos[tono]}`}>{children}</span>
}

export function BarraProgreso({ valor, total, etiqueta }: { valor: number; total: number; etiqueta: string }) {
  const pct = Math.round((100 * valor) / total)
  return (
    <div>
      <div className="mb-1.5 flex items-center justify-between text-xs font-medium text-texto-suave">
        <span>{etiqueta}</span>
        <span>
          {valor} / {total}
        </span>
      </div>
      <div className="h-2 overflow-hidden rounded-full bg-superficie-alt" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
        <div className="h-full rounded-full bg-oro transition-[width] duration-300" style={{ width: `${pct}%` }} />
      </div>
    </div>
  )
}

export function Espera({ etiqueta = 'Cargando…' }: { etiqueta?: string }) {
  return (
    <div className="flex items-center justify-center gap-3 py-12 text-texto-suave" role="status">
      <span className="size-5 animate-spin rounded-full border-2 border-current border-t-transparent" aria-hidden />
      {etiqueta}
    </div>
  )
}
